/**
 * "Agenda da clínica" (ADR-0028): a Cliqfy escolhe, para cada parte da tela da
 * recepção, a operação SQL que a atende. É o que deixa o fluxo do CRM igual
 * para qualquer sistema de clínica: trocar o SQL não muda o CRM.
 *
 * O servidor confere cada operação antes de gravar (tipo, publicada, número de
 * parâmetros, uma conexão só) e devolve o problema por parte; nada é gravado
 * enquanto houver um.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErroDaApi,
  type AgendaDaClinica as Mapa,
  type OperacaoDaApi,
  ESCRITAS_CONFERIDAS,
  PARAMETROS_DA_AGENDA,
  type CampoComPadrao,
  type ConferenciaDaEscrita,
  type ConferenciasDaAgenda,
  type EscritaConferida,
  type PadroesDaAgenda,
} from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import {
  useAgendaDaClinica,
  useApi,
  useListaDaAgendaDaClinica,
  useTodasAsOperacoesDeDados,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import { Botao, Campo, CampoSelecao, Selo } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  LISTA_DO_PADRAO,
  PADROES_DA_AGENDA,
  PARTES_DA_AGENDA,
  opcoesDoPadrao,
  campoVisivel,
  comVisivel,
  conferenciasMudaram,
  conferenciasParaGravar,
  mapaParaGravar,
  padroesMudaram,
  padroesParaGravar,
  mudou,
  operacoesDaParte,
  problemasDoMapa,
} from '../lib/agenda-da-clinica';
import './agenda-da-clinica.css';

export function AgendaDaClinica(): ReactNode {
  const mapa = useAgendaDaClinica(true);
  const operacoes = useTodasAsOperacoesDeDados(true);
  return (
    <section className="agenda-da-clinica" aria-labelledby="agenda-da-clinica-titulo">
      <div className="agenda-da-clinica__cabeca">
        <h2 id="agenda-da-clinica-titulo" className="agenda-da-clinica__titulo">
          Agenda da clínica
        </h2>
        <p className="agenda-da-clinica__apoio">
          Qual operação atende cada parte da tela da recepção: buscar paciente, vagas, agendar.
          Todas usam a mesma conexão com a clínica.
        </p>
      </div>
      {mapa.isPending || operacoes.isPending ? (
        <Carregando forma="lista" linhas={4} rotulo="Lendo a agenda da clínica…" />
      ) : mapa.isError || operacoes.isError ? (
        <Faixa tom="bloqueado">
          {recadoDeErro(mapa.error ?? operacoes.error, {
            generica: 'Não foi possível ler a agenda da clínica.',
          })}
        </Faixa>
      ) : (
        <Formulario gravado={mapa.data} operacoes={operacoes.data.itens} />
      )}
    </section>
  );
}

function Formulario({
  gravado,
  operacoes,
}: {
  gravado: Mapa;
  operacoes: readonly OperacaoDaApi[];
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const { recarregarMe } = useSessao();
  const [rascunho, definirRascunho] = useState<Record<string, string>>({ ...gravado.operacoes });
  /** O mapa gravado de quando a edição começou: o rascunho é comparado com ele. */
  const [base, definirBase] = useState<Record<string, string>>({ ...gravado.operacoes });
  /** A versão da base: vai no salvar, e o servidor recusa se outra pessoa salvou no meio. */
  const [versaoDaBase, definirVersaoDaBase] = useState(gravado.versao);
  const [recado, definirRecado] = useState<string | null>(null);
  // Os padrões do agendamento (#1187): os códigos que o formulário da recepção marca de início.
  const [padroes, definirPadroes] = useState<PadroesDaAgenda>({ ...gravado.padroes });
  const [basePadroes, definirBasePadroes] = useState<PadroesDaAgenda>({ ...gravado.padroes });
  // As conferências das escritas (#1187): a leitura que confere e os campos.
  const [conferencias, definirConferencias] = useState<ConferenciasDaAgenda>({
    ...gravado.conferencias,
  });
  const [baseConferencias, definirBaseConferencias] = useState<ConferenciasDaAgenda>({
    ...gravado.conferencias,
  });
  /*
   * Outra pessoa da Cliqfy salvou enquanto a tela estava aberta. Sem edição
   * em curso, a tela passa a mostrar o novo; com edição, avisa e não deixa
   * salvar por cima, que desfaria a mudança dela sem ninguém ver.
   */
  // A versão cobre o mapa e os padrões (#1187): qualquer um dos dois mudado por outra pessoa conta.
  const outraVersao = gravado.versao !== versaoDaBase;
  const editando =
    mudou(rascunho, base) ||
    padroesMudaram(padroes, basePadroes) ||
    conferenciasMudaram(conferencias, baseConferencias);
  const usarVersaoSalva = (): void => {
    definirRascunho({ ...gravado.operacoes });
    definirBase({ ...gravado.operacoes });
    definirPadroes({ ...gravado.padroes });
    definirBasePadroes({ ...gravado.padroes });
    definirConferencias({ ...gravado.conferencias });
    definirBaseConferencias({ ...gravado.conferencias });
    definirVersaoDaBase(gravado.versao);
    gravar.reset();
  };
  useEffect(() => {
    if (outraVersao && !editando) {
      definirRascunho({ ...gravado.operacoes });
      definirBase({ ...gravado.operacoes });
      definirPadroes({ ...gravado.padroes });
      definirBasePadroes({ ...gravado.padroes });
      definirConferencias({ ...gravado.conferencias });
      definirBaseConferencias({ ...gravado.conferencias });
      definirVersaoDaBase(gravado.versao);
    }
  }, [
    outraVersao,
    editando,
    gravado.operacoes,
    gravado.padroes,
    gravado.conferencias,
    gravado.versao,
  ]);

  const gravar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      return api.gravarAgendaDaClinica({
        corpo: {
          operacoes: mapaParaGravar(rascunho),
          // Os padrões só vão quando mudaram: sem eles, o servidor guarda os que já tinha.
          ...(padroesMudaram(padroes, gravado.padroes ?? {})
            ? { padroes: padroesParaGravar(padroes) }
            : {}),
          ...(conferenciasMudaram(conferencias, gravado.conferencias ?? {})
            ? { conferencias: conferenciasParaGravar(conferencias) }
            : {}),
          ...(versaoDaBase ? { versao: versaoDaBase } : {}),
        },
      });
    },
    onMutate: () => definirRecado(null),
    onError: (erro) => {
      // Outra pessoa salvou no meio: relê o mapa, e a faixa do conflito aparece.
      if (outraPessoaSalvou(erro)) {
        void cache.invalidateQueries({ queryKey: chaves.agendaDaClinica() });
      }
    },
    onSuccess: async (salvo) => {
      cache.setQueryData(chaves.agendaDaClinica(), salvo);
      definirRascunho({ ...salvo.operacoes });
      definirBase({ ...salvo.operacoes });
      definirVersaoDaBase(salvo.versao);
      definirPadroes({ ...salvo.padroes });
      definirBasePadroes({ ...salvo.padroes });
      definirConferencias({ ...salvo.conferencias });
      definirBaseConferencias({ ...salvo.conferencias });
      // A recepção recebe o mapa pelo /v1/me: sem recarregar, a tela dela ficaria velha.
      await recarregarMe();
      definirRecado('Agenda da clínica salva. A recepção já usa as operações novas.');
    },
  });

  const problemas = gravar.isError ? problemasDoMapa(gravar.error) : null;
  const temPorParte = problemas && Object.keys(problemas.porParte).length > 0;
  // A recusa do servidor também é conflito: a releitura do mapa pode ainda estar a caminho.
  const conflito = (outraVersao && editando) || outraPessoaSalvou(gravar.error);
  const alterado =
    !conflito &&
    (mudou(rascunho, gravado.operacoes) ||
      padroesMudaram(padroes, gravado.padroes ?? {}) ||
      conferenciasMudaram(conferencias, gravado.conferencias ?? {}));

  return (
    <form
      className="agenda-da-clinica__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (alterado && !gravar.isPending) gravar.mutate();
      }}
    >
      <ul className="agenda-da-clinica__partes">
        {PARTES_DA_AGENDA.map((parte) => {
          const opcoes = operacoesDaParte(parte, operacoes, rascunho[parte.parte]);
          const id = `agenda-da-clinica-${parte.parte}`;
          return (
            <li key={parte.parte} className="agenda-da-clinica__parte">
              <div className="agenda-da-clinica__sobre">
                <span className="agenda-da-clinica__nome">
                  {parte.nome}
                  <Selo tom={parte.grava ? 'atencao' : 'neutro'}>
                    {parte.grava ? 'Grava' : 'Lê'}
                  </Selo>
                </span>
                <span className="agenda-da-clinica__ajuda">
                  {parte.ajuda} {recebe(parte.parametros)}
                </span>
              </div>
              <CampoSelecao
                id={id}
                rotuloOculto={`Operação de ${parte.nome}`}
                value={rascunho[parte.parte] ?? ''}
                erro={problemas?.porParte[parte.parte]}
                onChange={(e) => {
                  const valor = e.currentTarget.value;
                  definirRascunho((atual) => ({ ...atual, [parte.parte]: valor }));
                }}
              >
                <option value="">Não ligada</option>
                {opcoes.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.situacao === 'publicada' ? op.nome : `${op.nome} (não publicada)`}
                  </option>
                ))}
              </CampoSelecao>
              {(ESCRITAS_CONFERIDAS as readonly string[]).includes(parte.parte) && (
                <ConferenciaDaParte
                  parte={parte.parte as EscritaConferida}
                  nome={parte.nome}
                  leituras={operacoes.filter(
                    (op) => op.tipo === 'leitura' && op.situacao === 'publicada',
                  )}
                  valor={conferencias[parte.parte as EscritaConferida]}
                  erro={problemas?.porConferencia[parte.parte]}
                  aoMudar={(valor) =>
                    definirConferencias((atual) => ({ ...atual, [parte.parte]: valor }))
                  }
                />
              )}
            </li>
          );
        })}
      </ul>

      <fieldset className="agenda-da-clinica__padroes">
        <legend className="agenda-da-clinica__nome">Padrões do agendamento</legend>
        <p className="agenda-da-clinica__ajuda">
          O que o formulário de agendar já traz marcado, escolhido nas listas da clínica. A recepção
          pode trocar; o convênio e o plano do paciente vinculado vencem. Vazio, o campo começa em
          branco. Sem "Campo visível", o campo some do agendamento e vai sempre com o padrão.
        </p>
        <div className="agenda-da-clinica__grade-dos-padroes">
          {PADROES_DA_AGENDA.map(({ chave, rotulo, exemplo }) => {
            const semValor = (padroes[chave]?.trim() ?? '') === '';
            return (
              <div key={chave} className="agenda-da-clinica__padrao">
                <EscolhaDoPadrao
                  chave={chave}
                  rotulo={rotulo}
                  exemplo={exemplo}
                  valor={padroes[chave] ?? ''}
                  convenio={padroes.convenio?.trim() ?? ''}
                  ligadas={gravado.operacoes}
                  erro={problemas?.porParte[`padroes.${chave}`]}
                  aoMudar={(valor) =>
                    definirPadroes((atual) => ({
                      ...atual,
                      [chave]: valor,
                      // Os planos são do convênio: trocado o convênio, o plano recomeça.
                      ...(chave === 'convenio' && valor !== atual.convenio ? { plano: '' } : {}),
                    }))
                  }
                />
                <label
                  className="agenda-da-clinica__visivel"
                  title={semValor ? 'Sem padrão, o campo sempre aparece.' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={campoVisivel(padroes, chave)}
                    disabled={semValor}
                    onChange={(e) => {
                      const visivel = e.currentTarget.checked;
                      definirPadroes((atual) => comVisivel(atual, chave, visivel));
                    }}
                  />
                  Campo visível
                </label>
              </div>
            );
          })}
        </div>
      </fieldset>

      {conflito && (
        <Faixa tom="atencao">
          Outra pessoa salvou a agenda enquanto você editava. Para não desfazer a mudança dela, use
          a versão salva e refaça a sua.{' '}
          <Botao variante="secundario" miudo onClick={usarVersaoSalva}>
            Usar a versão salva
          </Botao>
        </Faixa>
      )}

      {gravar.isError && !outraPessoaSalvou(gravar.error) && (
        <Faixa tom="bloqueado">
          {temPorParte
            ? `Nada foi gravado. ${[...(problemas?.geral ?? []), 'Corrija as partes marcadas.'].join(' ')}`
            : recadoDeErro(gravar.error, {
                generica: 'Não foi possível gravar a agenda da clínica.',
              })}
        </Faixa>
      )}

      <div className="agenda-da-clinica__rodape">
        <p className="agenda-da-clinica__recado" role="status" aria-live="polite">
          {recado ?? ''}
        </p>
        <Botao
          variante="primario"
          type="submit"
          desabilitado={!alterado || gravar.isPending}
          motivo={
            gravar.isPending
              ? 'Conferindo as operações no serviço…'
              : conflito
                ? 'Outra pessoa salvou antes: use a versão salva.'
                : 'Nada mudou.'
          }
        >
          {gravar.isPending ? 'Salvando…' : 'Salvar agenda'}
        </Botao>
      </div>
    </form>
  );
}

/**
 * O padrão de um campo (#1187): o dropdown com a lista da clínica. Sem a lista ligada no mapa, ou com o sistema da clínica fora,
 * o código se digita, como a recepção faz quando a lista não está ligada.
 */
function EscolhaDoPadrao({
  chave,
  rotulo,
  exemplo,
  valor,
  convenio,
  ligadas,
  erro,
  aoMudar,
}: {
  chave: CampoComPadrao;
  rotulo: string;
  exemplo: string;
  valor: string;
  convenio: string;
  ligadas: Readonly<Record<string, string>>;
  erro: string | undefined;
  aoMudar: (valor: string) => void;
}): ReactNode {
  const lista = LISTA_DO_PADRAO[chave];
  const ligada = ligadas[lista] !== undefined;
  const leitura = useListaDaAgendaDaClinica(lista, convenio, ligada);
  const id = `agenda-da-clinica-padrao-${chave}`;
  const selecao = (
    opcoes: readonly { valor: string; rotulo: string }[],
    vazio: string,
    desabilitado = false,
  ) => (
    <CampoSelecao
      id={id}
      rotulo={rotulo}
      value={valor}
      erro={erro}
      disabled={desabilitado}
      onChange={(e) => aoMudar(e.currentTarget.value)}
    >
      <option value="">{vazio}</option>
      {opcoes.map((opcao) => (
        <option key={opcao.valor} value={opcao.valor}>
          {opcao.rotulo}
        </option>
      ))}
    </CampoSelecao>
  );
  if (lista === 'planos' && ligada && convenio === '') {
    return selecao([], 'Escolha o convênio primeiro', true);
  }
  if (!ligada || leitura.isError) {
    return (
      <Campo
        id={id}
        rotulo={`${rotulo} (código)`}
        placeholder={exemplo}
        maxLength={30}
        ajuda={
          !ligada
            ? 'A lista não está ligada: digite o código.'
            : 'A lista da clínica não respondeu: digite o código.'
        }
        ajudaEmbaixo
        value={valor}
        erro={erro}
        onChange={(e) => aoMudar(e.currentTarget.value)}
      />
    );
  }
  if (leitura.isPending) return selecao([], 'Carregando…', true);
  return selecao(opcoesDoPadrao(leitura.data.itens, valor), 'Sem padrão');
}

/**
 * A conferência de uma escrita (#1187): a leitura que confere o que foi
 * gravado, com a mesma vaga, e os campos comparados. No cancelar, os campos têm
 * de voltar vazios. Sem leitura escolhida, a escrita responde como antes.
 */
function ConferenciaDaParte({
  parte,
  nome,
  leituras,
  valor,
  erro,
  aoMudar,
}: {
  parte: EscritaConferida;
  nome: string;
  leituras: readonly OperacaoDaApi[];
  valor: ConferenciaDaEscrita | undefined;
  erro: string | undefined;
  aoMudar: (valor: ConferenciaDaEscrita) => void;
}): ReactNode {
  const atual = valor ?? { operacao: '', campos: [] };
  const id = `agenda-da-clinica-conferencia-${parte}`;
  const campos: readonly string[] = PARAMETROS_DA_AGENDA.agendar;
  return (
    <details className="agenda-da-clinica__conferencia" open={atual.operacao !== '' || undefined}>
      <summary>
        Conferir a gravação{atual.operacao ? ` · ${atual.campos.length} campos` : ''}
      </summary>
      <p className="agenda-da-clinica__ajuda">
        {parte === 'liberar_vaga'
          ? 'Depois de cancelar, o CRM lê a vaga e confere que estes campos voltaram vazios.'
          : 'Depois de gravar, o CRM lê a vaga e confere que estes campos voltaram como foram enviados.'}{' '}
        Se não baterem, nada muda no CRM e a tela avisa.
      </p>
      <CampoSelecao
        id={id}
        rotulo={`Leitura que confere ${nome.toLowerCase()}`}
        value={atual.operacao}
        erro={erro}
        onChange={(e) => aoMudar({ ...atual, operacao: e.currentTarget.value })}
      >
        <option value="">Sem conferência</option>
        {leituras.map((op) => (
          <option key={op.id} value={op.id}>
            {op.nome}
          </option>
        ))}
      </CampoSelecao>
      {atual.operacao !== '' && (
        <fieldset className="agenda-da-clinica__campos-conferidos">
          <legend className="agenda-da-clinica__ajuda">Campos comparados</legend>
          {campos.map((campo) => (
            <label key={campo} className="agenda-da-clinica__visivel">
              <input
                type="checkbox"
                checked={atual.campos.includes(campo)}
                onChange={(e) => {
                  const marcado = e.currentTarget.checked;
                  aoMudar({
                    ...atual,
                    campos: marcado
                      ? [...atual.campos, campo]
                      : atual.campos.filter((c) => c !== campo),
                  });
                }}
              />
              <code>{campo}</code>
            </label>
          ))}
        </fieldset>
      )}
    </details>
  );
}

const outraPessoaSalvou = (erro: unknown): boolean =>
  erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_VERSAO';

/**
 * O que a parte recebe, numa linha. Com muitos campos (o agendar leva vinte e
 * um), a lista inteira empurrava a tela: a operação declara pelo nome só os que
 * a clínica grava, e a mensagem do servidor diz o que falta.
 */
function recebe(parametros: readonly string[]): string {
  if (parametros.length === 0) return 'Não recebe parâmetros.';
  if (parametros.length <= 6) return `Recebe: ${parametros.join(', ')}.`;
  return `Recebe, pelo nome, os campos que a clínica grava, entre os ${parametros.length} da parte (${parametros.slice(0, 3).join(', ')}…).`;
}
